/**
 * 80 mm thermal layouts: the bill/receipt, and the QR slip for a dining
 * session.
 *
 * Both are plain HTML strings printed through the hidden iframe in
 * `lib/print.ts`. The API has no rendered-receipt payload of any kind, so this
 * IS the receipt renderer — see `lib/api.ts` gap #4.
 */

import { qrSlipPayload } from '@sonauto/qr';
import type { QrSlipInput } from '@sonauto/qr';
import { escapeHtml, printMoney } from '../../lib/print';
import type { OrderItemRow, OrderRow, SessionDetailResponse } from '../../lib/types';

export interface ReceiptHeader {
  readonly restaurantName: string;
  readonly branchName: string;
  readonly tableLabel: string;
  readonly cashierName: string;
  readonly invoiceNo: string | null;
  readonly printedAt: number;
  /** True for a pre-bill ("tạm tính"), which must be visibly NOT a receipt. */
  readonly provisional: boolean;
}

function dt(at: number): string {
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(at);
}

function itemRow(item: OrderItemRow, exponent: number): string {
  const gift = item.is_gift === 1;
  return (
    `<tr><td colspan="3">${escapeHtml(item.product_name_snapshot)}${gift ? ' <b>(TẶNG)</b>' : ''}</td></tr>` +
    `<tr><td class="mod">${item.quantity} × ${printMoney(item.unit_price_minor, exponent)}</td>` +
    `<td class="num">${item.discount_minor > 0 ? `-${printMoney(item.discount_minor, exponent)}` : ''}</td>` +
    `<td class="num">${printMoney(gift ? 0 : item.line_total_minor, exponent)}</td></tr>`
  );
}

/**
 * Build a bill for one dining session (all its orders, cancelled lines
 * excluded — a cancelled line is on the document at zero, but a guest reading
 * their bill should not see dishes they never got).
 */
export function buildReceiptHtml(header: ReceiptHeader, detail: SessionDetailResponse): string {
  const orders = detail.orders.filter((o) => o.status !== 'CANCELLED' && o.status !== 'VOIDED');
  const exponent = orders[0]?.currency_exponent ?? 0;
  const currency = orders[0]?.currency ?? 'VND';

  const body = orders
    .map((order: OrderRow & { items: OrderItemRow[] }) => {
      const lines = order.items
        .filter((i) => i.status !== 'CANCELLED' && i.status !== 'VOIDED')
        .map((i) => itemRow(i, exponent))
        .join('');
      return `<tr><td colspan="3" class="muted tiny">Đơn ${escapeHtml(order.order_no)}</td></tr>${lines}`;
    })
    .join('');

  const totals = orders.reduce(
    (acc, order) => ({
      subtotal: acc.subtotal + order.subtotal_minor,
      discount: acc.discount + order.item_discount_minor + order.order_discount_minor,
      service: acc.service + order.service_charge_minor,
      tax: acc.tax + order.tax_minor,
      rounding: acc.rounding + order.rounding_minor,
      total: acc.total + order.total_minor,
    }),
    { subtotal: 0, discount: 0, service: 0, tax: 0, rounding: 0, total: 0 },
  );

  const row = (label: string, amount: number, big = false): string =>
    `<tr><td colspan="2"${big ? ' class="big"' : ''}>${escapeHtml(label)}</td>` +
    `<td class="num${big ? ' big' : ''}">${printMoney(amount, exponent)}</td></tr>`;

  return [
    `<h1>${escapeHtml(header.restaurantName)}</h1>`,
    `<h2>${escapeHtml(header.branchName)}</h2>`,
    header.provisional
      ? '<p class="center big">PHIẾU TẠM TÍNH</p><p class="center tiny">Không có giá trị thay hoá đơn</p>'
      : `<p class="center">HOÁ ĐƠN${header.invoiceNo === null ? '' : ` ${escapeHtml(header.invoiceNo)}`}</p>`,
    '<div class="rule"></div>',
    `<div class="tiny">Bàn: ${escapeHtml(header.tableLabel)}</div>`,
    `<div class="tiny">Thu ngân: ${escapeHtml(header.cashierName)}</div>`,
    `<div class="tiny">Giờ in: ${dt(header.printedAt)}</div>`,
    `<div class="tiny">Mã phiên: ${escapeHtml(detail.session.session_code)}</div>`,
    '<div class="rule"></div>',
    '<table><thead><tr><th>Món</th><th class="num">CK</th><th class="num">T.Tiền</th></tr></thead>',
    `<tbody>${body}</tbody></table>`,
    '<div class="rule"></div>',
    '<table>',
    row('Tạm tính', totals.subtotal),
    totals.discount > 0 ? row('Giảm giá', -totals.discount) : '',
    totals.service > 0 ? row('Phí phục vụ', totals.service) : '',
    totals.tax > 0 ? row('Thuế', totals.tax) : '',
    totals.rounding !== 0 ? row('Làm tròn', totals.rounding) : '',
    row('TỔNG CỘNG', totals.total, true),
    row('Đã thanh toán', detail.balance.paidMinor),
    row('Còn lại', detail.balance.outstandingMinor, true),
    '</table>',
    '<div class="rule"></div>',
    `<p class="center tiny">Đơn vị tiền: ${escapeHtml(currency)}</p>`,
    '<p class="center">Cảm ơn quý khách!</p>',
  ].join('');
}

/**
 * The QR slip a guest scans to order from their phone.
 *
 * The Vietnamese copy, the validity window and the do-not-share warning come
 * from `qrSlipPayload` — computed SERVER-SIDE by `POST
 * /v1/dining-sessions/:id/qr-token` (the `slip` field of the response) so the
 * wording is identical wherever a slip is produced. `qrSvg` is the rendered QR
 * image (see `lib/qr.ts`); `null` prints an explicit "no code" line rather
 * than a blank square, because a blank square looks like a printer fault.
 */
export interface QrSlipPayloadLike {
  readonly restaurantName: string;
  readonly branchName: string;
  readonly tableDisplayName: string;
  readonly sessionCode: string;
  readonly qrUrl: string;
  readonly issuedAt: number;
  readonly expiresAt: number;
  readonly instructionsVi: readonly string[];
  readonly warningVi: string;
}

export function buildQrSlipHtmlFromPayload(payload: QrSlipPayloadLike, qrSvg: string | null): string {
  return [
    `<h1>${escapeHtml(payload.restaurantName)}</h1>`,
    `<h2>${escapeHtml(payload.branchName)}</h2>`,
    '<div class="rule"></div>',
    `<p class="center big">${escapeHtml(payload.tableDisplayName)}</p>`,
    `<p class="center">Mã phiên: <b>${escapeHtml(payload.sessionCode)}</b></p>`,
    qrSvg === null
      ? '<p class="center tiny">[ Không tạo được hình QR — dùng đường dẫn bên dưới ]</p>'
      : `<div class="qr" style="width:48mm;height:48mm;margin:3mm auto 1mm">${qrSvg}</div>`,
    `<p class="center tiny">${escapeHtml(payload.qrUrl)}</p>`,
    '<div class="rule"></div>',
    '<ol class="tiny">',
    payload.instructionsVi.map((line) => `<li>${escapeHtml(line)}</li>`).join(''),
    '</ol>',
    '<div class="rule"></div>',
    `<p class="tiny"><b>${escapeHtml(payload.warningVi)}</b></p>`,
    `<p class="center tiny">In lúc ${dt(payload.issuedAt)} · hết hạn ${dt(payload.expiresAt)}</p>`,
  ].join('');
}

/** Compose the payload locally (offline fallback), then reuse the one layout. */
export function buildQrSlipHtml(input: QrSlipInput, qrSvg: string | null): string {
  return buildQrSlipHtmlFromPayload(qrSlipPayload(input), qrSvg);
}
